import React, { useState, Fragment, useEffect, useRef } from "react";

// ui
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Box,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Tooltip,
} from "@mui/material";
import { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/system";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import AddIcon from "@mui/icons-material/Add";
import EditNoteIcon from "@mui/icons-material/EditNote";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CancelIcon from "@mui/icons-material/Cancel";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DoDisturbAltIcon from "@mui/icons-material/DoDisturbAlt";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import AddIcCallIcon from "@mui/icons-material/AddIcCall";
import CallIcon from "@mui/icons-material/Call";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import {
  dataDifine,
  subDataDifine,
  DataColumn,
  TableColumn,
  SubTableColumn,
  EditDataDifine,
  SubDataColumn,
} from "../config/dataInterface";
import { getOneYearList } from "../lib/other";

// api
import { getPresignedGetPicture } from "../api/presignedURL";
import {
  getOptionSelects,
  getOptionSelectsTossUp,
  getOptionSelectsOutside,
} from "../api/optionSelect";
import { getPaymentConfirmExcel } from "../api/paymentConfirmExcel";

// jotai
import { useAtomValue, useSetAtom } from "jotai";
import { AppAtoms } from "../lib/store";

import { CONST } from "../config/const";

// ─── cell rendering helpers (from DataTable) ──────────────────────
const PAYMENT_STATE_CHIP: Record<string, { bg: string; fg: string }> = {
  正常: { bg: "var(--st-06-paid-bg)", fg: "var(--st-06-paid)" },
  正常以外: { bg: "var(--st-10-returned-bg)", fg: "var(--st-10-returned)" },
  上位店支払明細の取込なし: {
    bg: "var(--st-05-await-pay-bg)",
    fg: "var(--st-05-await-pay)",
  },
  申込日から上位店入金単価を検索できません: {
    bg: "var(--st-02-pending-bg)",
    fg: "var(--st-02-pending)",
  },
  "支払方法、申込日、リストIDからパートナー支払単価を検索できません": {
    bg: "var(--st-07-pay-prep-bg)",
    fg: "var(--st-07-pay-prep)",
  },
  上位店明細と商材単価の入金額不一致: {
    bg: "var(--st-10-returned-bg)",
    fg: "var(--st-10-returned)",
  },
  売上金額がマイナス: { bg: "var(--st-09-hold-bg)", fg: "var(--st-09-hold)" },
  "上位店支払明細の取込はあるが、エントリー情報なし": {
    bg: "var(--st-01-draft-bg)",
    fg: "var(--st-01-draft)",
  },
  "パートナーへの支払のみのはずが、上位店支払明細の取込があり": {
    bg: "var(--st-04-billed-bg)",
    fg: "var(--st-04-billed)",
  },
};
const TEXT_CHIP: Record<string, { bg: string; fg: string }> = {
  通常: { bg: "var(--sem-success-bg)", fg: "var(--sem-success)" },
  公開: { bg: "var(--sem-success-bg)", fg: "var(--sem-success)" },
  停止中: { bg: "var(--st-11-cancelled-bg)", fg: "var(--st-11-cancelled)" },
  非公開: { bg: "var(--st-11-cancelled-bg)", fg: "var(--st-11-cancelled)" },
  閉鎖中: { bg: "var(--st-11-cancelled-bg)", fg: "var(--st-11-cancelled)" },
  商材停止中: { bg: "var(--st-09-hold-bg)", fg: "var(--st-09-hold)" },
};
const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
function formatCellDate(val: string): string {
  if (ISO_DATETIME_RE.test(val)) {
    const m = val.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (m) return `${m[1]}-${m[2]}-${m[3]} ${m[4]}:${m[5]}`;
  }
  if (ISO_DATE_RE.test(val)) {
    return val;
  }
  return val;
}
function renderChipBox(label: string, chip: { bg: string; fg: string }) {
  return (
    // catalog #09 po-chip + chip-wrap: inline-flex / align-items:center / min-height:22px
    // padding: 2px var(--sp-2,8px) / border-radius:11px / fs-11 / fw-semibold
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        minHeight: "22px",
        padding: "2px var(--sp-2, 8px)",
        borderRadius: "11px",
        fontSize: "var(--fs-11)",
        fontWeight: "var(--fw-semibold)",
        lineHeight: 1.3,
        bgcolor: chip.bg,
        color: chip.fg,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
}
function getRowBg(
  pathname: string,
  row: dataDifine,
  index: number,
): string | undefined {
  if (pathname === "/toss_up") {
    const s = row.toss_up_status as string;
    const isNet =
      row.category_length === 1 &&
      row.category_length_name === "インターネット";
    if (s === "トスアップ") return undefined;
    if (s === "クロージング-成功（未エントリー）")
      return isNet ? "var(--row-cl-noentry-strong)" : "var(--row-cl-noentry)";
    if (s === "不出")
      return isNet ? "var(--row-fushutsu-strong)" : "var(--row-fushutsu)";
    if (s === "時節")
      return isNet ? "var(--row-jisetsu-strong)" : "var(--row-jisetsu)";
    if (s === "見込み")
      return isNet ? "var(--row-mikomi-strong)" : "var(--row-mikomi)";
    if (s === "架電中") return "var(--row-calling)";
    return index % 2 === 1 ? "var(--color-ai-pale)" : undefined;
  }
  if (pathname === "/follow_call") {
    const s = row.follow_call_status as string;
    if (s === "不出") return "var(--row-fushutsu)";
    if (s === "時節") return "var(--row-jisetsu)";
    if (s === "見込み") return "var(--row-mikomi)";
    return index % 2 === 1 ? "var(--color-ai-pale)" : undefined;
  }
  return index % 2 === 1 ? "var(--color-ai-pale)" : undefined;
}
export function generatePageRange(
  current: number,
  total: number,
): (number | "...")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, "...", total];
  if (current >= total - 3)
    return [1, "...", total - 4, total - 3, total - 2, total - 1, total];
  return [1, "...", current - 1, current, current + 1, "...", total];
}

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: "1.5px 1.5px rgba(115, 115, 115, 0.8)",
    fontSize: 11,
  },
}));

interface SubTableRowProps {
  subTableColumn: SubTableColumn;
  getSubTableDetail: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  };
  setGetSubTableDetail: (value: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  }) => void;
  getSubSubTableName: {
    name: string;
    key: { [key: string]: string };
    subData: subDataDifine[];
  };
  setGetSubTableEditDirect: (value: EditDataDifine | null) => void;
  setGetSubSubTableName: (value: {
    name: string;
    key: { [key: string]: string };
    subData: subDataDifine[];
  }) => void;
  getSubSubTableDetail: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  };
  setGetSubSubTableDetail: (value: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  }) => void;
  setGetSubSubTableAdd: (value: {
    name: string;
    row: dataDifine | null;
  }) => void;
  setGetSubSubTableEditDirect: (value: EditDataDifine | null) => void;
  setTableOriginalFunc: (value: {
    name: string;
    key: { [key: string]: string };
    subTableColumnName?: string;
  }) => void;
  index: number;
  sub_data: subDataDifine;
}
function SubTableRow(props: SubTableRowProps) {
  const {
    subTableColumn,
    getSubTableDetail,
    setGetSubTableDetail,
    getSubSubTableName,
    setGetSubTableEditDirect,
    setGetSubSubTableName,
    getSubSubTableDetail,
    setGetSubSubTableDetail,
    setGetSubSubTableAdd,
    setGetSubSubTableEditDirect,
    setTableOriginalFunc,
    index,
    sub_data,
  } = props;
  const loginUser = useAtomValue(AppAtoms.loginUser);
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const setIsLoading = useSetAtom(AppAtoms.isLoading);
  const pathname = useAtomValue(AppAtoms.pathname);
  const [openTooltip, setOpenTooltip] = useState(false);
  async function downloadContractPicture(destination_key: string) {
    setIsLoading(true);
    const header_location = await getPresignedGetPicture(destination_key);
    if (header_location != "None") {
      const url = header_location;
      const link = document.createElement("a");
      link.href = header_location;
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      link.parentNode?.removeChild(link);
    }
    setIsLoading(false);
  }
  async function downloadPaymentExcel(
    payment_year: string,
    payment_month: string,
    payment_sale_company_id: number,
  ) {
    setIsLoading(true);
    const responce = await getPaymentConfirmExcel({
      payment_year,
      payment_month,
      payment_sale_company_id,
    });
    if (responce != "None") {
      const url = responce;
      const link = document.createElement("a");
      link.href = responce;
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      link.parentNode?.removeChild(link);
    }
    setIsLoading(false);
  }
  return (
    <Fragment>
      <TableRow
        key={"subTableData" + index}
        sx={{
          "& > *": {
            borderBottom: "unset",
          },
          "&:hover .MuiTableCell-root": {
            backgroundColor: "var(--state-hover)",
          },
        }}
        style={{
          backgroundColor:
            index % 2 === 1 ? CONST.tableBackgroundColor : undefined,
        }}
      >
        {subTableColumn.subDataColumn?.map(
          (subDataColumn: SubDataColumn, i) => {
            if (subDataColumn.table) {
              return (
                <LightTooltip
                  onClose={() => setOpenTooltip(false)}
                  open={openTooltip && subDataColumn.key === "entry_url"}
                  title="コピーしました"
                >
                  <TableCell
                    key={"tableSubData" + i}
                    align={
                      ["edit_check", "check", "downloadPaymentExcel"].includes(
                        subDataColumn.tableValue ?? "",
                      )
                        ? "center"
                        : "left"
                    }
                    component="th"
                    scope="row"
                  >
                    {subDataColumn.tableValue == "downloadPaymentExcel" ? (
                      <Box
                        className="table-body"
                        onClick={() => {
                          downloadPaymentExcel(
                            sub_data["payment_year"] as string,
                            sub_data["payment_month"] as string,
                            sub_data["payment_sale_company_id"] as number,
                          );
                        }}
                      >
                        <IconButton
                          sx={{ height: "30px", width: "30px" }}
                          aria-label="detail row"
                          size="small"
                        >
                          <FileDownloadIcon fontSize="medium" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box
                        className="table-body"
                        onClick={() => {
                          if (
                            sub_data.option_type === 4 &&
                            sub_data[subDataColumn.key] &&
                            sub_data[subDataColumn.key] != "" &&
                            subDataColumn.key == "customer_info_value"
                          ) {
                            downloadContractPicture(
                              sub_data[subDataColumn.key] as string,
                            );
                          }
                        }}
                      >
                        {sub_data.option_type === 4 &&
                        sub_data[subDataColumn.key] &&
                        sub_data[subDataColumn.key] != "" &&
                        subDataColumn.key == "customer_info_value"
                          ? (sub_data[subDataColumn.key] as string)
                              .split("/")
                              .pop()
                          : subTableColumn.subTableColumnName ===
                                "閲覧可能グループ一覧" &&
                              subDataColumn.key === "group_name" &&
                              sub_data["sale_company_name"] != null &&
                              sub_data["sale_company_name"] !== ""
                            ? `${sub_data["sale_company_name"]} / ${sub_data["group_name"]}`
                            : sub_data[subDataColumn.key]}
                        {sub_data.option_type === 4 &&
                        sub_data[subDataColumn.key] &&
                        sub_data[subDataColumn.key] != "" &&
                        subDataColumn.key == "customer_info_value" ? (
                          <IconButton
                            sx={{
                              height: "30px",
                              width: "30px",
                            }}
                            aria-label="detail row"
                            size="small"
                          >
                            <FileDownloadIcon fontSize="medium" />
                          </IconButton>
                        ) : null}
                        {subDataColumn.key === "entry_url" ? (
                          <IconButton
                            aria-label="detail row"
                            size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(
                                sub_data[subDataColumn.key] as string,
                              );
                              setOpenTooltip(true);
                            }}
                          >
                            <ContentCopyIcon fontSize="medium" />
                          </IconButton>
                        ) : null}
                      </Box>
                    )}
                  </TableCell>
                </LightTooltip>
              );
            }
          },
        )}

        {subTableColumn.subTable.map((subTable, subTable_i) => {
          let dataDetailKey: { [key: string]: string } = {};
          let openSubTable = true;
          subTableColumn.detailKey.map((key) => {
            dataDetailKey[key] = sub_data[key] as string;
            if (getSubSubTableName.key[key] !== sub_data[key]) {
              openSubTable = false;
            }
          });
          return (
            <>
              <TableCell
                key={"tableColumnSunSub_" + subTable_i}
                align="center"
                component="th"
                scope="row"
                sx={{
                  minWidth: subTable.subTableColumnNameWidth,
                  maxWidth: isSmartPhone
                    ? subTable.subTableColumnNameWidth
                    : undefined,
                }}
              >
                {String(
                  sub_data[subTable.subTableColumnLength + "_disable"],
                ) === "0" ? (
                  <Box
                    className="table-body"
                    data-testid="subtable-unavailable"
                  >
                    -
                  </Box>
                ) : pathname === "/toss_up" &&
                  sub_data[subTable.subTableColumnLength] === 1 ? (
                  <Box className="table-body">
                    {
                      sub_data[
                        subTable.subTableColumnLength + "_name"
                      ] as string
                    }
                  </Box>
                ) : (
                  <IconButton
                    disabled={
                      sub_data[subTable.subTableColumnLength] == undefined
                        ? true
                        : !subTable.subTableAdd &&
                          (sub_data[
                            subTable.subTableColumnLength
                          ] as number) === 0
                    }
                    sx={{ height: "30px", width: "30px" }}
                    aria-label="expand row"
                    size="small"
                    onClick={() => {
                      if (
                        getSubSubTableName.name ===
                          subTable.subTableColumnName &&
                        openSubTable
                      ) {
                        setGetSubSubTableName({
                          name: "",
                          key: {},
                          subData: [],
                        });
                      } else {
                        setGetSubSubTableName({
                          name: subTable.subTableColumnName,
                          key: dataDetailKey,
                          subData: [],
                        });
                      }
                    }}
                  >
                    {getSubSubTableName.name === subTable.subTableColumnName &&
                    openSubTable ? (
                      <KeyboardArrowUpIcon fontSize="medium" />
                    ) : (
                      <KeyboardArrowDownIcon fontSize="medium" />
                    )}
                  </IconButton>
                )}
              </TableCell>
            </>
          );
        })}
        {loginUser &&
        subTableColumn.subTableEditAuth.includes(loginUser.authority) &&
        subTableColumn.subTableEditDirect ? (
          <TableCell
            key="subTableDataDetail"
            align="center"
            component="th"
            scope="row"
          >
            <IconButton
              sx={{ height: "30px", width: "30px" }}
              aria-label="detail row"
              size="small"
              onClick={async () => {
                if (subTableColumn.subTableColumnName === "顧客情報一覧") {
                  if (
                    sub_data.option_type === 0 ||
                    sub_data.option_type === 3 ||
                    sub_data.option_type === 4
                  ) {
                    setGetSubTableEditDirect({
                      status: "before",
                      editType:
                        sub_data.option_type === 0
                          ? sub_data.multi_text_flg === 1
                            ? "customer_multitext"
                            : "customer_text"
                          : sub_data.option_type === 3
                            ? "customer_date"
                            : "customer_picture",
                      tableId: sub_data.id as string,
                      id: sub_data.id as number,
                      key: sub_data.customer_info_key as string,
                      name: sub_data.customer_info_key as string,
                      value: sub_data.customer_info as string,
                      editValue: sub_data.customer_info as string,
                      editPhoneNumber:
                        sub_data.tel_flg === 1
                          ? (sub_data[
                              subTableColumn.subTableEditDirectKey
                            ] as string)
                          : "",
                      editName:
                        sub_data.name_flg === 1
                          ? (sub_data[
                              subTableColumn.subTableEditDirectKey
                            ] as string)
                          : "",
                      editCustomerName:
                        sub_data.company_name_flg === 1
                          ? (sub_data[
                              subTableColumn.subTableEditDirectKey
                            ] as string)
                          : "",
                      tel_flg: sub_data.tel_flg as number,
                      name_flg: sub_data.name_flg as number,
                      company_name_flg: sub_data.company_name_flg as number,
                      half_width_number_flg:
                        sub_data.half_width_number_flg as number,
                      customerList: [],
                      payment_method_id: "",
                      service_id: sub_data.service_id as string,
                      customer_picture: null,
                    });
                  } else {
                    setIsLoading(true);
                    setGetSubTableEditDirect({
                      status: "editing",
                      editType:
                        sub_data.option_type === 0
                          ? sub_data.multi_text_flg === 1
                            ? "customer_multitext"
                            : "customer_text"
                          : sub_data.option_type === 1
                            ? "customer_select_one"
                            : sub_data.option_type === 2
                              ? "customer_select_multiple"
                              : sub_data.option_type === 3
                                ? "customer_date"
                                : "customer_picture",
                      tableId: sub_data.id as string,
                      id: sub_data.id as number,
                      key: sub_data.customer_info_key as string,
                      name: sub_data.customer_info_key as string,
                      value: sub_data.customer_info as string,
                      editValue: sub_data.customer_info as string,
                      editPhoneNumber:
                        sub_data.tel_flg === 1
                          ? (sub_data[
                              subTableColumn.subTableEditDirectKey
                            ] as string)
                          : "",
                      editName:
                        sub_data.name_flg === 1
                          ? (sub_data[
                              subTableColumn.subTableEditDirectKey
                            ] as string)
                          : "",
                      editCustomerName:
                        sub_data.company_name_flg === 1
                          ? (sub_data[
                              subTableColumn.subTableEditDirectKey
                            ] as string)
                          : "",
                      tel_flg: sub_data.tel_flg as number,
                      name_flg: sub_data.name_flg as number,
                      company_name_flg: sub_data.company_name_flg as number,
                      half_width_number_flg:
                        sub_data.half_width_number_flg as number,
                      customerList: [],
                      payment_method_id: "",
                      service_id: sub_data.service_id as string,
                      customer_picture: null,
                    });
                    // 質問の選択肢一覧取得
                    const optionSelects =
                      pathname == "/toss_up"
                        ? await getOptionSelectsTossUp(
                            sub_data.option_id as number,
                          )
                        : sub_data.entry_method === 0
                          ? await getOptionSelects(sub_data.option_id as number)
                          : sub_data.entry_method === 1
                            ? await getOptionSelectsOutside(
                                sub_data.option_id as number,
                              )
                            : [];

                    setGetSubTableEditDirect({
                      status: "before",
                      editType:
                        sub_data.option_type === 0
                          ? sub_data.multi_text_flg === 1
                            ? "customer_multitext"
                            : "customer_text"
                          : sub_data.option_type === 1
                            ? "customer_select_one"
                            : sub_data.option_type === 2
                              ? "customer_select_multiple"
                              : sub_data.option_type === 3
                                ? "customer_date"
                                : "customer_picture",
                      tableId: sub_data.id as string,
                      id: sub_data.id as number,
                      key: sub_data.customer_info_key as string,
                      name: sub_data.customer_info_key as string,
                      value: sub_data.customer_info as string,
                      editValue: sub_data.customer_info as string,
                      editPhoneNumber:
                        sub_data.tel_flg === 1
                          ? (sub_data[
                              subTableColumn.subTableEditDirectKey
                            ] as string)
                          : "",
                      editName:
                        sub_data.name_flg === 1
                          ? (sub_data[
                              subTableColumn.subTableEditDirectKey
                            ] as string)
                          : "",
                      editCustomerName:
                        sub_data.company_name_flg === 1
                          ? (sub_data[
                              subTableColumn.subTableEditDirectKey
                            ] as string)
                          : "",
                      tel_flg: sub_data.tel_flg as number,
                      name_flg: sub_data.name_flg as number,
                      company_name_flg: sub_data.company_name_flg as number,
                      customerList: optionSelects,
                      payment_method_id: "",
                      service_id: sub_data.service_id as string,
                      customer_picture: null,
                    });
                    setIsLoading(false);
                  }
                } else if (subTableColumn.subTableColumnName === "質問一覧") {
                  setGetSubTableEditDirect({
                    status: "before",
                    editType: "text",
                    tableId: sub_data.id as string,
                    id: Number(
                      sub_data.entry_form_id ??
                        sub_data.toss_up_form_id ??
                        sub_data.outside_entry_form_id,
                    ),
                    key: subTableColumn.subTableEditDirectKey as string,
                    name: subTableColumn.subTableColumnName as string,
                    value: sub_data.id as string,
                    editValue: sub_data.id as string,
                    editPhoneNumber: "",
                    editName: "",
                    editCustomerName: "",
                    tel_flg: 0,
                    name_flg: 0,
                    company_name_flg: 0,
                    half_width_number_flg: 0,
                    customerList: [],
                    payment_method_id: "",
                    service_id: "",
                    customer_picture: null,
                  });
                } else {
                  setGetSubTableEditDirect({
                    status: "before",
                    editType:
                      sub_data.editType === "list"
                        ? "list"
                        : sub_data.editType === "multitext"
                          ? "multitext"
                          : "text",
                    tableId: sub_data.id as string,
                    id: sub_data.id as number,
                    key: subTableColumn.subTableEditDirectKey as string,
                    name: subTableColumn.subTableColumnName as string,
                    value: sub_data[
                      subTableColumn.subTableEditDirectKey
                    ] as string,
                    editValue: sub_data[
                      subTableColumn.subTableEditDirectKey
                    ] as string,
                    editPhoneNumber: "",
                    editName: "",
                    editCustomerName: "",
                    tel_flg: 0,
                    name_flg: 0,
                    company_name_flg: 0,
                    half_width_number_flg: 0,
                    customerList: [],
                    payment_method_id: sub_data.payment_method_id as string,
                    service_id: sub_data.service_id as string,
                    customer_picture: null,
                  });
                }
              }}
            >
              <EditNoteIcon fontSize="medium" />
            </IconButton>
          </TableCell>
        ) : null}
        {subTableColumn.subTableDetail ? (
          <TableCell
            key="subTableDataDetail"
            align="center"
            component="th"
            scope="row"
          >
            <IconButton
              sx={{ height: "30px", width: "30px" }}
              aria-label="detail row"
              size="small"
              onClick={() => {
                let dataDetailKey: { [key: string]: string } = {};
                subTableColumn.detailKey.map((key) => {
                  dataDetailKey[key] = sub_data[key] as string;
                });
                setGetSubTableDetail({
                  ...getSubTableDetail,
                  key: dataDetailKey,
                  name: subTableColumn.subTableDetailTitle,
                });
              }}
            >
              <MoreHorizIcon fontSize="medium" />
            </IconButton>
          </TableCell>
        ) : null}
        {subTableColumn.tableFunction.map((tableFunction) => {
          if (loginUser && tableFunction.auth.includes(loginUser.authority)) {
            if (tableFunction.name === "削除") {
              return (
                <TableCell
                  key="tableDataCancell"
                  align="center"
                  component="th"
                  scope="row"
                >
                  <IconButton
                    sx={{ height: "30px", width: "30px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: {
                        [key: string]: string;
                      } = {};
                      subTableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = sub_data[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                        subTableColumnName: subTableColumn.subTableColumnName,
                      });
                    }}
                  >
                    <DeleteIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            }
          }
        })}
      </TableRow>
      {subTableColumn.subTable.map((subTable, subTable_i) => {
        let dataDetailKey: { [key: string]: string } = {};
        let openSubTable = true;
        subTableColumn.detailKey.map((key) => {
          dataDetailKey[key] = sub_data[key] as string;
          if (getSubSubTableName.key[key] !== sub_data[key]) {
            openSubTable = false;
          }
        });
        if (
          getSubSubTableName.name === subTable.subTableColumnName &&
          openSubTable
        ) {
          return (
            <SubTable
              open={
                getSubSubTableName.name === subTable.subTableColumnName &&
                openSubTable
              }
              subTableColumn={subTable}
              getSubTableName={getSubSubTableName}
              getSubTableDetail={getSubSubTableDetail}
              setGetSubTableDetail={setGetSubSubTableDetail}
              setGetSubTableEditDirect={setGetSubSubTableEditDirect}
              getSubSubTableName={{
                name: "",
                key: {},
                subData: [],
              }}
              setGetSubSubTableName={() => {}}
              getSubSubTableDetail={{
                name: "",
                key: {},
                dataDetail: null,
              }}
              setGetSubSubTableDetail={() => {}}
              setGetSubSubTableAdd={() => {}}
              setGetSubSubTableEditDirect={() => {}}
              mainRow={sub_data}
              setGetSubTableAdd={setGetSubSubTableAdd}
              setTableOriginalFunc={setTableOriginalFunc}
              nested={true}
            />
          );
        }
      })}
    </Fragment>
  );
}

interface SubTableProps {
  open: boolean;
  getSubTableName: {
    name: string;
    key: { [key: string]: string };
    subData: subDataDifine[] | null;
  };
  subTableColumn: SubTableColumn;
  getSubTableDetail: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  };
  setGetSubTableDetail: (value: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  }) => void;
  getSubSubTableName: {
    name: string;
    key: { [key: string]: string };
    subData: subDataDifine[];
  };
  setGetSubTableEditDirect: (value: EditDataDifine | null) => void;
  setGetSubSubTableName: (value: {
    name: string;
    key: { [key: string]: string };
    subData: subDataDifine[];
  }) => void;
  getSubSubTableDetail: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  };
  setGetSubSubTableDetail: (value: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  }) => void;
  setGetSubSubTableAdd: (value: {
    name: string;
    row: dataDifine | null;
  }) => void;
  setGetSubSubTableEditDirect: (value: EditDataDifine | null) => void;
  setTableOriginalFunc: (value: {
    name: string;
    key: { [key: string]: string };
    subTableColumnName?: string;
  }) => void;
  setGetSubTableAdd?: (value: { name: string; row: dataDifine }) => void;
  mainRow?: dataDifine;
  nested?: boolean;
}
function SubTable(props: SubTableProps) {
  const {
    open,
    getSubTableName,
    subTableColumn,
    getSubTableDetail,
    setGetSubTableDetail,
    getSubSubTableName,
    setGetSubTableEditDirect,
    setGetSubSubTableName,
    getSubSubTableDetail,
    setGetSubSubTableDetail,
    setGetSubSubTableAdd,
    setGetSubSubTableEditDirect,
    setTableOriginalFunc,
    setGetSubTableAdd,
    mainRow,
    nested = false,
  } = props;
  const loginUser = useAtomValue(AppAtoms.loginUser);
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const pathname = useAtomValue(AppAtoms.pathname);
  const subTableWrapperRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null); // 要件C: タイトルBar sticky 幅固定・thead top CSS変数追跡
  // 最寄りスクロール祖先を返す（サブサブ時は外箱 wrapper が返る）
  const getScrollContainer = (el: HTMLElement): Element | null => {
    let p: HTMLElement | null = el.parentElement;
    while (p) {
      const { overflowY } = window.getComputedStyle(p);
      if (overflowY === "auto" || overflowY === "scroll") return p;
      p = p.parentElement;
    }
    return null;
  };
  // 要件 A/B: 親行上端 auto-scroll（A・先行）→ 箱top実測ベース maxHeight（B・後続）
  // 要件 C/3': サブサブ時 container=外箱→タイトルBar高も headerH に加算（外箱の二段sticky帯直下に親行を揃える）
  const adjustSubTable = (wrapper: HTMLElement, container: Element): void => {
    const parentRow = wrapper.closest("tr")?.previousElementSibling as HTMLElement | null;
    const scrollRoot = wrapper.closest('[data-testid="table-scroll-container"]');
    const isOuterBox = container !== scrollRoot;   // top-level: container===scrollRoot→false／サブサブ: container=外箱wrapper≠scrollRoot→true
    const outerTitle = isOuterBox
      ? (container.querySelector(":scope > * > [data-subtable-title]") as HTMLElement | null)  // 外箱直下の自titleのみ・孫サブテーブルのtitleを拾わない
      : null;
    const titleBarH = outerTitle?.offsetHeight ?? 0;   // top-level は 0
    const headerH = titleBarH + ((container.querySelector("thead") as HTMLElement | null)?.offsetHeight ?? 47);
    const ESCAPE = 16; // 横スクロールバー逃げ
    wrapper.style.maxWidth = `${container.clientWidth}px`;
    // (A) auto-scroll: 親行を固定見出し直下へ寄せる（冪等）
    const cRect = container.getBoundingClientRect();
    if (parentRow) {
      const pRect = parentRow.getBoundingClientRect();
      (container as HTMLElement).scrollTop += Math.round(pRect.top - cRect.top - headerH);
    }
    // (B) scroll後の確定 box top から maxHeight を計算（td padding/border を実測吸収）
    const wRect = wrapper.getBoundingClientRect();
    wrapper.style.maxHeight = `${Math.max(0, container.clientHeight - (wRect.top - cRect.top) - ESCAPE)}px`;
    // (C) タイトル幅固定（sticky left:0 の可動域確保）+ thead top CSS変数設定（1166）
    if (titleRef.current) {
      titleRef.current.style.width = `${container.clientWidth - 26}px`;
    }
    wrapper.style.setProperty("--sub-thead-top", `${titleRef.current?.offsetHeight ?? 0}px`);
    // 案(c): 横overflowが縦スクロールバー幅相当(≤16px)なら横バーを出さず(hidden)、genuine wide(>16px)は横スクロール保持(auto)
    const SCROLLBAR_GUTTER_MAX = 16; // 縦バー物理幅上限(Chrome~15/Win~17px)。この範囲の横overflow=縦バーが内側幅を食っただけのspurious
    const hOverflow = wrapper.scrollWidth - wrapper.clientWidth;
    const nextOverflowX = hOverflow <= SCROLLBAR_GUTTER_MAX ? "hidden" : "auto";
    if (wrapper.style.overflowX !== nextOverflowX) {
      wrapper.style.overflowX = nextOverflowX; // 値変化時のみ書込＝ResizeObserverでの無限振動を防ぐ
    }
  };
  const subAddDisabled =
    !mainRow ||
    mainRow.id === "-" ||
    (subTableColumn.subTableAddTitle === "解約SMS追加" &&
      mainRow.cancell_action !== "手動解約可能") ||
    (subTableColumn.subTableAddTitle === "閲覧可能商材追加" &&
      mainRow.authority !== "営業MGR" &&
      mainRow.authority !== "パートナー") ||
    (subTableColumn.subTableAddTitle === "閲覧可能グループ追加" &&
      mainRow.authority !== "営業MGR" &&
      mainRow.authority !== "パートナー") ||
    (subTableColumn.subTableAddTitle === "ログインパートナー追加" &&
      mainRow.login_sale_company !== "指定したログインパートナーのみ") ||
    (subTableColumn.subTableAddTitle === "選択肢追加" &&
      (mainRow.screen_column_system_default !== "独自項目" ||
        (mainRow.screen_column_type !== "プルダウン" &&
          mainRow.screen_column_type !== "チェックボックス"))) ||
    (pathname === "/outside_entry_form" &&
      subTableColumn.subTableAddTitle === "グループ追加" &&
      mainRow.entry_sale_company_group !== "指定する") ||
    (subTableColumn.subTableAddTitle === "リストID追加" &&
      (mainRow.customer_list_id_flg === "使用しない" ||
        mainRow.customer_list_id_flg === "-"));

  useEffect(() => {
    if (!open || !isSmartPhone) return;
    const handleScroll = () => {
      if (subTableWrapperRef.current) {
        subTableWrapperRef.current.style.transform = `translateX(${window.scrollX}px)`;
      }
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (subTableWrapperRef.current) {
        subTableWrapperRef.current.style.transform = "";
      }
    };
  }, [open, isSmartPhone]);

  useEffect(() => {
    if (!open || isSmartPhone) return;
    const wrapper = subTableWrapperRef.current;
    if (!wrapper) return;
    const container = getScrollContainer(wrapper);
    if (!container) return;
    const observer = new ResizeObserver(() => adjustSubTable(wrapper, container));
    observer.observe(container);
    observer.observe(wrapper);
    return () => {
      observer.disconnect();
      wrapper.style.maxWidth = "";
      wrapper.style.maxHeight = "";
      wrapper.style.overflowX = "";
    };
  }, [open, isSmartPhone]);

  return (
    <TableRow>
      <TableCell
        colSpan={99}
        sx={{
          p: 0,
          backgroundColor: "white",
        }}
      >
        <Collapse
          in={open}
          timeout="auto"
          unmountOnExit
          onEntered={() => {
            if (!isSmartPhone && subTableWrapperRef.current) {
              const container = getScrollContainer(subTableWrapperRef.current);
              if (!container) return;
              adjustSubTable(subTableWrapperRef.current, container);
            }
          }}
        >
          <Box
            ref={subTableWrapperRef}
            sx={{
              position: isSmartPhone ? undefined : "sticky",
              left: isSmartPhone ? undefined : 0,
              maxWidth: isSmartPhone ? "100%" : "calc(100vw - 50px)",
              overflowX: "auto",
              overscrollBehaviorX: "contain",
              overflowY: isSmartPhone ? undefined : "auto",
            }}
          >
            <Box
              sx={{
                border: "1px solid var(--color-border)",
                minWidth: "max-content",
                margin: "8px 12px",
                bgcolor: "var(--color-washi, #fafaf8)",
              }}
            >
              {(!nested || subTableColumn.subTableAdd) && (
                <Box
                  ref={titleRef}
                  data-subtable-title=""
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    bgcolor: "var(--color-ai)",
                    px: "12px",
                    py: "8px",
                    borderBottom: "1px solid var(--color-washi, #fafaf8)",
                    position: "sticky",
                    top: 0,
                    left: "12px",
                    zIndex: 3,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 700,
                      color: "var(--color-white)",
                    }}
                  >
                    {subTableColumn.subTableColumnName}
                  </Typography>
                  {subTableColumn.subTableAdd &&
                    mainRow &&
                    loginUser &&
                    (nested ||
                      subTableColumn.subTableAuth.includes(
                        loginUser.authority,
                      )) && (
                      <Button
                        disabled={subAddDisabled}
                        size="small"
                        variant="text"
                        aria-label="追加"
                        sx={{
                          fontSize: "11px",
                          fontWeight: 600,
                          color: "var(--color-white)",
                          minWidth: 0,
                          py: 0.25,
                          px: 1,
                        }}
                        onClick={() =>
                          setGetSubTableAdd?.({
                            name: subTableColumn.subTableAddTitle,
                            row: mainRow,
                          })
                        }
                      >
                        + {subTableColumn.subTableAddLabel ?? "追加"}
                      </Button>
                    )}
                </Box>
              )}
              <Table size="small" aria-label="purchases" sx={{ "& thead .MuiTableCell-root": { position: "sticky", top: "var(--sub-thead-top, 0px)", zIndex: 2, backgroundColor: "var(--color-ai)" } }}>
                <TableHead>
                  <TableRow>
                    {subTableColumn.subDataColumn?.map((subDataColumn, i) => {
                      if (subDataColumn.table) {
                        return (
                          <TableCell
                            sx={{
                              backgroundColor: "var(--color-ai)",
                            }}
                            key={"subTableColumn" + i}
                            align="left"
                          >
                            <Box className="table-header">
                              {subDataColumn.name}
                            </Box>
                          </TableCell>
                        );
                      }
                    })}
                    {subTableColumn.subTable.map((subTable, subTable_i) => {
                      return (
                        <>
                          <TableCell
                            sx={{
                              backgroundColor: "var(--color-ai)",
                              minWidth: subTable.subTableColumnNameWidth,
                              maxWidth: isSmartPhone
                                ? subTable.subTableColumnNameWidth
                                : undefined,
                            }}
                            key={"tableColumnSubSub" + subTable_i}
                            align="center"
                          >
                            <Box className="table-header">
                              {subTable.subTableColumnName}
                            </Box>
                          </TableCell>
                        </>
                      );
                    })}
                    {loginUser &&
                    subTableColumn.subTableEditAuth.includes(
                      loginUser.authority,
                    ) &&
                    subTableColumn.subTableEditDirect ? (
                      <TableCell
                        sx={{
                          backgroundColor: "var(--color-ai)",
                        }}
                        key="subTableEditDirectColumn"
                        align="center"
                      >
                        <Box className="table-header">
                          {subTableColumn.subTableEditTitle}
                        </Box>
                      </TableCell>
                    ) : null}
                    {subTableColumn.subTableDetail ? (
                      <TableCell
                        sx={{
                          backgroundColor: "var(--color-ai)",
                        }}
                        key="subTableColumnDetail"
                        align="center"
                      >
                        <Box className="table-header">詳細</Box>
                      </TableCell>
                    ) : null}
                    {subTableColumn.tableFunction.map((tableFunction, i) => {
                      if (
                        loginUser &&
                        tableFunction.auth.includes(loginUser.authority)
                      ) {
                        return (
                          <TableCell
                            sx={{
                              backgroundColor: "var(--color-ai)",
                            }}
                            key={"tableColumnFunc" + i}
                            align="center"
                          >
                            <Box className="table-header">
                              {tableFunction.name}
                            </Box>
                          </TableCell>
                        );
                      }
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getSubTableName.subData?.map((sub_data, index) => {
                    return (
                      <SubTableRow
                        subTableColumn={subTableColumn}
                        getSubTableDetail={getSubTableDetail}
                        setGetSubTableDetail={setGetSubTableDetail}
                        getSubSubTableName={getSubSubTableName}
                        setGetSubTableEditDirect={setGetSubTableEditDirect}
                        setGetSubSubTableName={setGetSubSubTableName}
                        getSubSubTableDetail={getSubSubTableDetail}
                        setGetSubSubTableDetail={setGetSubSubTableDetail}
                        setGetSubSubTableAdd={setGetSubSubTableAdd}
                        setGetSubSubTableEditDirect={
                          setGetSubSubTableEditDirect
                        }
                        setTableOriginalFunc={setTableOriginalFunc}
                        index={index}
                        sub_data={sub_data}
                      />
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
}

export interface RowProps {
  row: dataDifine;
  index: number;
  tableColumn: TableColumn;
  screenDataColumn: DataColumn[];
  getDataDetail: (key: { [key: string]: string }, edit: boolean) => void;
  getSubTableName: {
    name: string;
    key: { [key: string]: string };
    subData: subDataDifine[];
  };
  setGetSubTableName: (value: {
    name: string;
    key: { [key: string]: string };
    subData: subDataDifine[];
  }) => void;
  getSubTableDetail: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  };
  setGetSubTableDetail: (value: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  }) => void;
  setGetSubTableAdd: (value: { name: string; row: dataDifine | null }) => void;
  getSubSubTableName: {
    name: string;
    key: { [key: string]: string };
    subData: subDataDifine[];
  };
  setGetSubTableEditDirect: (value: EditDataDifine | null) => void;
  setGetSubSubTableName: (value: {
    name: string;
    key: { [key: string]: string };
    subData: subDataDifine[];
  }) => void;
  getSubSubTableDetail: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  };
  setGetSubSubTableDetail: (value: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  }) => void;
  setGetSubSubTableAdd: (value: {
    name: string;
    row: dataDifine | null;
  }) => void;
  setGetSubSubTableEditDirect: (value: EditDataDifine | null) => void;
  switchTableCheckBox: (dataKey: { [key: string]: string }) => void;
  editIDs: number[];
  setEditIDs: (value: number[]) => void;
  setTableOriginalFunc: (value: {
    name: string;
    key: { [key: string]: string };
    subTableColumnName?: string;
  }) => void;
  onDetailOpen?: (rowId: string) => void;
}

function Row(props: RowProps) {
  const {
    row,
    index,
    tableColumn,
    screenDataColumn,
    getDataDetail,
    getSubTableName,
    setGetSubTableName,
    getSubTableDetail,
    setGetSubTableDetail,
    setGetSubTableAdd,
    setGetSubTableEditDirect,
    getSubSubTableName,
    setGetSubSubTableName,
    getSubSubTableDetail,
    setGetSubSubTableDetail,
    setGetSubSubTableAdd,
    setGetSubSubTableEditDirect,
    switchTableCheckBox,
    editIDs,
    setEditIDs,
    setTableOriginalFunc,
    onDetailOpen,
  } = props;

  const loginUser = useAtomValue(AppAtoms.loginUser);
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const pathname = useAtomValue(AppAtoms.pathname);
  const [openTooltip, setOpenTooltip] = useState("");
  const setIsLoading = useSetAtom(AppAtoms.isLoading);
  async function downloadPaymentExcel(
    payment_year: string,
    payment_month: string,
    payment_sale_company_id: number,
  ) {
    setIsLoading(true);
    const responce = await getPaymentConfirmExcel({
      payment_year,
      payment_month,
      payment_sale_company_id,
    });
    if (responce != "None") {
      const url = responce;
      const link = document.createElement("a");
      link.href = responce;
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      link.parentNode?.removeChild(link);
    }
    setIsLoading(false);
  }
  return (
    <Fragment>
      <TableRow
        sx={{
          "& > *": {
            borderBottom: "unset",
          },
          "&:hover .MuiTableCell-root": {
            backgroundColor: "var(--state-hover)",
          },
        }}
        style={{
          backgroundColor: getRowBg(pathname, row, index),
        }}
        data-row-id={row.id != null ? String(row.id) : undefined}
      >
        {screenDataColumn.map((dataColumn, i) => {
          if (dataColumn.table) {
            if (dataColumn.key === "contract_number_months") {
              return getOneYearList().map((yearMonth, year_i) => {
                return (
                  <TableCell
                    key={"tableYearMonthData" + year_i}
                    align="left"
                    component="th"
                    scope="row"
                    sx={{
                      minWidth: isSmartPhone
                        ? dataColumn.tableMinWidth
                        : undefined,
                      maxWidth: isSmartPhone
                        ? dataColumn.tableMinWidth
                        : undefined,
                    }}
                  >
                    <Box className="table-body">
                      {row[dataColumn.key + year_i] as string}
                    </Box>
                  </TableCell>
                );
              });
            } else {
              if (
                loginUser &&
                dataColumn.tableDetailAuth.includes(loginUser.authority)
              ) {
                return (
                  <LightTooltip
                    onClose={() => setOpenTooltip("")}
                    open={openTooltip == dataColumn.key}
                    title="コピーしました"
                  >
                    <TableCell
                      key={"tableData" + i}
                      align={
                        ["edit_check", "check", "downloadPaymentExcel"].includes(
                          dataColumn.tableValue ?? "",
                        )
                          ? "center"
                          : "left"
                      }
                      component="th"
                      scope="row"
                      sx={{
                        minWidth: isSmartPhone
                          ? dataColumn.tableMinWidth
                          : undefined,
                        maxWidth: isSmartPhone
                          ? dataColumn.tableMinWidth
                          : undefined,
                      }}
                      onClick={() => {
                        if (dataColumn.key === "password") {
                          navigator.clipboard.writeText(
                            row["copy_password"] as string,
                          );
                          setOpenTooltip(dataColumn.key);
                        } else if (
                          dataColumn.key === "outside_entry_url" ||
                          dataColumn.key === "outside_cancell_url"
                        ) {
                          navigator.clipboard.writeText(
                            row[dataColumn.key] as string,
                          );
                          setOpenTooltip(dataColumn.key);
                        }
                      }}
                    >
                      {dataColumn.tableValue == "text" ? (
                        <Box className="table-body">
                          {typeof row[dataColumn.key] === "string"
                            ? formatCellDate(row[dataColumn.key] as string)
                            : (row[dataColumn.key] as string)}
                          {dataColumn.key === "password" ||
                          ((dataColumn.key === "outside_entry_url" ||
                            dataColumn.key === "outside_cancell_url") &&
                            row[dataColumn.key] !== "-") ? (
                            <IconButton aria-label="detail row" size="small">
                              <ContentCopyIcon fontSize="medium" />
                            </IconButton>
                          ) : null}
                        </Box>
                      ) : dataColumn.tableValue == "status_chip" ? (
                        (() => {
                          const paymentStateChipMap: {
                            [key: string]: { bg: string; fg: string };
                          } = {
                            正常: {
                              bg: "var(--st-06-paid-bg, #e6f4ee)",
                              fg: "var(--st-06-paid, #22664a)",
                            },
                            正常以外: {
                              bg: "var(--st-10-returned-bg, #fbeae8)",
                              fg: "var(--st-10-returned, #a02e27)",
                            },
                            上位店支払明細の取込なし: {
                              bg: "var(--st-05-await-pay-bg, #fdf3e0)",
                              fg: "var(--st-05-await-pay, #92450a)",
                            },
                            申込日から上位店入金単価を検索できません: {
                              bg: "var(--st-02-pending-bg, #f7f0dc)",
                              fg: "var(--st-02-pending, #7a5f15)",
                            },
                            "支払方法、申込日、リストIDからパートナー支払単価を検索できません":
                              {
                                bg: "var(--st-07-pay-prep-bg, #ebecf6)",
                                fg: "var(--st-07-pay-prep, #4b4f83)",
                              },
                            上位店明細と商材単価の入金額不一致: {
                              bg: "var(--st-10-returned-bg, #fbeae8)",
                              fg: "var(--st-10-returned, #a02e27)",
                            },
                            売上金額がマイナス: {
                              bg: "var(--st-09-hold-bg, #f5ece5)",
                              fg: "var(--st-09-hold, #7c4e33)",
                            },
                            "上位店支払明細の取込はあるが、エントリー情報なし":
                              {
                                bg: "var(--st-01-draft-bg, #eef0f2)",
                                fg: "var(--st-01-draft, #4b5563)",
                              },
                            "パートナーへの支払のみのはずが、上位店支払明細の取込があり":
                              {
                                bg: "var(--st-04-billed-bg, #e8f1f8)",
                                fg: "var(--st-04-billed, #27547c)",
                              },
                          };
                          const label = row[dataColumn.key] as string;
                          const chip = paymentStateChipMap[label] ?? {
                            bg: "var(--st-11-cancelled-bg, #f4f5f6)",
                            fg: "var(--st-11-cancelled, #5b6470)",
                          };
                          return (
                            <Box
                              data-testid="status-chip"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                borderRadius: "11px",
                                fontSize: 11,
                                fontWeight: 600,
                                whiteSpace: "normal",
                                lineHeight: 1.3,
                                minHeight: "22px",
                                px: "8px",
                                py: "2px",
                                textAlign: "left",
                                maxWidth: "220px",
                                bgcolor: chip.bg,
                                color: chip.fg,
                              }}
                            >
                              {label}
                            </Box>
                          );
                        })()
                      ) : dataColumn.tableValue == "text_chip" ? (
                        (() => {
                          const raw = row[dataColumn.key] as string;
                          const chip = TEXT_CHIP[raw];
                          return chip ? (
                            renderChipBox(raw, chip)
                          ) : (
                            <Box className="table-body">{raw}</Box>
                          );
                        })()
                      ) : dataColumn.tableValue == "check" ? (
                        <IconButton
                          sx={{ height: "30px", width: "30px" }}
                          aria-label="detail row"
                          size="small"
                        >
                          <Checkbox
                            disabled={row[dataColumn.key + "_disable"] != "1"}
                            checked={row[dataColumn.key] == "1"}
                            onClick={() => {
                              let dataDetailKey: { [key: string]: string } = {};
                              tableColumn.detailKey.map((key) => {
                                dataDetailKey[key] = row[key] as string;
                              });
                              dataDetailKey["key"] = dataColumn.key;
                              switchTableCheckBox(dataDetailKey);
                            }}
                            sx={{
                              "& .MuiSvgIcon-root": { fontSize: 18 },
                            }}
                          />
                        </IconButton>
                      ) : dataColumn.tableValue == "edit_check" ? (
                        <IconButton
                          sx={{ height: "30px", width: "30px" }}
                          aria-label="detail row"
                          size="small"
                        >
                          <Checkbox
                            disabled={row.id === "-"}
                            checked={editIDs.includes(row.id as number)}
                            onClick={() => {
                              let _editIDs = editIDs.concat();
                              if (editIDs.includes(row.id as number)) {
                                _editIDs = _editIDs.filter(
                                  (editID) => editID != row.id,
                                );
                              } else {
                                _editIDs.push(row.id as number);
                              }
                              setEditIDs(_editIDs);
                            }}
                            sx={{
                              "& .MuiSvgIcon-root": { fontSize: 18 },
                            }}
                          />
                        </IconButton>
                      ) : dataColumn.tableValue == "downloadPaymentExcel" ? (
                        <IconButton
                          disabled={
                            (row["payment_year"] as string) == "-" ||
                            (row["payment_month"] as string) == "-"
                          }
                          sx={{ height: "30px", width: "30px" }}
                          aria-label="detail row"
                          size="small"
                          onClick={() => {
                            downloadPaymentExcel(
                              row["payment_year"] as string,
                              row["payment_month"] as string,
                              row["payment_sale_company_id"] as number,
                            );
                          }}
                        >
                          <FileDownloadIcon fontSize="medium" />
                        </IconButton>
                      ) : null}
                    </TableCell>
                  </LightTooltip>
                );
              }
            }
          }
        })}

        {tableColumn.subTable.map((subTable, subTable_i) => {
          let dataDetailKey: { [key: string]: string } = {};
          let openSubTable = true;
          tableColumn.detailKey.map((key) => {
            dataDetailKey[key] = row[key] as string;
            if (getSubTableName.key[key] !== row[key]) {
              openSubTable = false;
            }
          });
          if (
            loginUser &&
            subTable.subTableAuth.includes(loginUser.authority)
          ) {
            return (
              <>
                <TableCell
                  key={"tableColumnSub_" + subTable_i + "_" + index}
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: subTable.subTableColumnNameWidth,
                    maxWidth: isSmartPhone
                      ? subTable.subTableColumnNameWidth
                      : undefined,
                  }}
                >
                  {String(row[subTable.subTableColumnLength + "_disable"]) ===
                  "0" ? (
                    <Box
                      className="table-body"
                      data-testid="subtable-unavailable"
                    >
                      -
                    </Box>
                  ) : pathname === "/toss_up" &&
                    row[subTable.subTableColumnLength] === 1 ? (
                    <Box className="table-body">
                      {row[subTable.subTableColumnLength + "_name"] as string}
                    </Box>
                  ) : (
                    <IconButton
                      disabled={
                        row[subTable.subTableColumnLength] == undefined ||
                        (!subTable.subTableAdd &&
                          (row[subTable.subTableColumnLength] as number) ===
                            0) ||
                        ((row[subTable.subTableColumnLength] as number) === 0 &&
                          row.id === "-")
                      }
                      sx={{ height: "30px", width: "30px" }}
                      aria-label="expand row"
                      size="small"
                      onClick={() => {
                        if (
                          getSubTableName.name ===
                            subTable.subTableColumnName &&
                          openSubTable
                        ) {
                          setGetSubTableName({
                            name: "",
                            key: {},
                            subData: [],
                          });
                        } else {
                          setGetSubTableName({
                            name: subTable.subTableColumnName,
                            key: dataDetailKey,
                            subData: [],
                          });
                        }
                      }}
                    >
                      {getSubTableName.name === subTable.subTableColumnName &&
                      openSubTable ? (
                        <KeyboardArrowUpIcon fontSize="medium" />
                      ) : (
                        <KeyboardArrowDownIcon fontSize="medium" />
                      )}
                    </IconButton>
                  )}
                </TableCell>
              </>
            );
          }
        })}
        {tableColumn.detail ? (
          <TableCell
            key="tableDataDetail"
            align="center"
            component="th"
            scope="row"
            sx={{
              minWidth: isSmartPhone ? "60px" : undefined,
              maxWidth: isSmartPhone ? "60px" : undefined,
            }}
          >
            <IconButton
              disabled={row.id === "-"}
              sx={{ height: "30px", width: "30px" }}
              aria-label="detail row"
              size="small"
              onClick={() => {
                let dataDetailKey: { [key: string]: string } = {};
                tableColumn.detailKey.map((key) => {
                  dataDetailKey[key] = row[key] as string;
                });
                onDetailOpen?.(row.id != null ? String(row.id) : '');
                getDataDetail(dataDetailKey, false);
              }}
            >
              <MoreHorizIcon fontSize="medium" />
            </IconButton>
          </TableCell>
        ) : null}
        {tableColumn.tableFunction.map((tableFunction) => {
          if (loginUser && tableFunction.auth.includes(loginUser.authority)) {
            if (tableFunction.name === "エントリー取消") {
              return (
                <TableCell
                  key="tableDataCancell"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={
                      row.opening_date_func !== "-" ||
                      row.contract_deactivation === 1
                    }
                    sx={{ height: "30px", width: "30px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                      });
                    }}
                  >
                    <DeleteIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "トスアップ複製") {
              return (
                <TableCell
                  key="tableDataCopy"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={
                      pathname === "/toss_up"
                        ? row.toss_up_status_func !== "クロージング-成功" &&
                          row.toss_up_status_func !== "クロージング-失敗" &&
                          row.toss_up_status_func !== "対象外" &&
                          row.toss_up_status_func !== "条件期間外" &&
                          row.toss_up_status_func !== "長期不出" &&
                          row.toss_up_status_func !==
                            "クロージング-成功（未エントリー）" &&
                          row.toss_up_status_func !==
                            "クロージング-成功（他社）"
                        : false
                    }
                    sx={{ height: "30px", width: "30px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                      });
                    }}
                  >
                    <ContentCopyIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "トスアップ取消") {
              return (
                <TableCell
                  key="tableDataCancell"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={
                      row.toss_up_status_func === "クロージング-成功" ||
                      row.toss_up_status_func === "クロージング-失敗" ||
                      row.toss_up_status_func == "対象外" ||
                      row.toss_up_status_func == "条件期間外" ||
                      row.toss_up_status_func == "長期不出" ||
                      row.toss_up_status_func ===
                        "クロージング-成功（未エントリー）" ||
                      row.toss_up_status_func === "クロージング-成功（他社）" ||
                      row.toss_up_status_func === "-" ||
                      row.toss_up_deactivation === 1
                    }
                    sx={{ height: "30px", width: "30px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                      });
                    }}
                  >
                    <DeleteIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "解約") {
              return (
                <TableCell
                  key="tableDataDelete"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={
                      row.cancell_action !== "手動解約可能" ||
                      row.apply_date_func === "-" ||
                      row.cancell_date_func !== "-" ||
                      row.contract_deactivation === 1
                    }
                    sx={{ height: "30px", width: "30px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                      });
                    }}
                  >
                    <CancelIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "パスワードリセット") {
              return (
                <TableCell
                  key="tableDataDelete"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={row.login_status == "ログイン不可能"}
                    sx={{ height: "30px", width: "30px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                      });
                    }}
                  >
                    <VpnKeyIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "クロージング") {
              return (
                <TableCell
                  key="tableDataClose"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={
                      row.toss_up_status_func == "クロージング-成功" ||
                      row.toss_up_status_func == "クロージング-失敗" ||
                      row.toss_up_status_func == "対象外" ||
                      row.toss_up_status_func == "条件期間外" ||
                      row.toss_up_status_func == "長期不出" ||
                      row.toss_up_status_func === "クロージング-成功（他社）" ||
                      row.toss_up_status_func === "-" ||
                      row.closable_flg === 0 ||
                      row.closable_flg === "0"
                    }
                    sx={{ height: "30px", width: "30px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                      });
                    }}
                  >
                    <AddCircleOutlineIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "取扱い停止・再開") {
              return (
                <TableCell
                  key="tableDataEnd"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={row.no_entry_flg_pd === 1}
                    sx={{ height: "30px", width: "30px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: {
                          ...dataDetailKey,
                          startStop:
                            row.no_entry_flg_sr === 1 ? "start" : "stop",
                        },
                      });
                    }}
                  >
                    {row.no_entry_flg_sr === 1 ? (
                      <PlayCircleOutlineIcon fontSize="medium" />
                    ) : (
                      <DoDisturbAltIcon fontSize="medium" />
                    )}
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "公開・非公開") {
              return (
                <TableCell
                  key="tableDataEnd"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={
                      row.id === "-" || row.site_status === "商材停止中"
                    }
                    sx={{ height: "30px", width: "30px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: {
                          ...dataDetailKey,
                          startStop:
                            row.site_status === "商材停止中" ||
                            row.site_status === "閉鎖中"
                              ? "start"
                              : "stop",
                        },
                      });
                    }}
                  >
                    {row.site_status === "商材停止中" ||
                    row.site_status === "閉鎖中" ? (
                      <PlayCircleOutlineIcon fontSize="medium" />
                    ) : (
                      <DoDisturbAltIcon fontSize="medium" />
                    )}
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "削除") {
              return (
                <TableCell
                  key="tableDataEnd"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={
                      pathname === "/follow_call_config" && row.id === "-"
                    }
                    sx={{ height: "30px", width: "30px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                      });
                    }}
                  >
                    <DeleteIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "項目削除") {
              return (
                <TableCell
                  key="tableDataEnd"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={
                      row.screen_column_system_default === "デフォルト項目"
                    }
                    sx={{ height: "30px", width: "30px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                      });
                    }}
                  >
                    <DeleteIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "コピー") {
              return (
                <TableCell
                  key="tableDataEnd"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={row.id === "-"}
                    sx={{ height: "30px", width: "30px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                      });
                    }}
                  >
                    <ContentCopyIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "個人情報削除") {
              return (
                <TableCell
                  key="tablePersonDataDelete"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={
                      row.cancell_date_func === "-" ||
                      row.customer_phone_number_func === "削除済"
                    }
                    sx={{ height: "30px", width: "30px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                      });
                    }}
                  >
                    <PersonOffIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "停止・再開") {
              return (
                <TableCell
                  key="tableDataEnd"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={row.follow_call_status === "フォローコール完了"}
                    sx={{ height: "30px", width: "30px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: {
                          ...dataDetailKey,
                          startStop:
                            row.follow_call_status ===
                            "フォローコールを行わない"
                              ? "start"
                              : "stop",
                        },
                      });
                    }}
                  >
                    {row.follow_call_status === "フォローコールを行わない" ? (
                      <AddIcCallIcon fontSize="medium" />
                    ) : (
                      <DoDisturbAltIcon fontSize="medium" />
                    )}
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "フォローコール") {
              return (
                <TableCell
                  key="tableDataEnd"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={
                      row.follow_call_status === "フォローコールを行わない" ||
                      row.follow_call_status === "フォローコール完了"
                    }
                    sx={{ height: "30px", width: "30px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                      });
                    }}
                  >
                    <CallIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "体制図") {
              return (
                <TableCell
                  key="tableDataChart"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    sx={{ height: "30px", width: "30px" }}
                    aria-label="chart row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                      });
                    }}
                  >
                    <AccountTreeIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "エントリー複製") {
              return (
                <TableCell
                  key="tableDataEntrycopy"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={row.contract_deactivation === 1}
                    sx={{ height: "30px", width: "30px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                      });
                    }}
                  >
                    <ContentCopyIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            }
          }
        })}
      </TableRow>
      {tableColumn.subTable.map((subTable, subTable_i) => {
        let dataDetailKey: { [key: string]: string } = {};
        let openSubTable = true;
        tableColumn.detailKey.map((key) => {
          dataDetailKey[key] = row[key] as string;
          if (getSubTableName.key[key] !== row[key]) {
            openSubTable = false;
          }
        });
        if (
          getSubTableName.name === subTable.subTableColumnName &&
          openSubTable
        ) {
          return (
            <SubTable
              open={
                getSubTableName.name === subTable.subTableColumnName &&
                openSubTable
              }
              subTableColumn={subTable}
              getSubTableName={getSubTableName}
              getSubTableDetail={getSubTableDetail}
              setGetSubTableDetail={setGetSubTableDetail}
              setGetSubTableEditDirect={setGetSubTableEditDirect}
              getSubSubTableName={getSubSubTableName}
              setGetSubSubTableName={setGetSubSubTableName}
              getSubSubTableDetail={getSubSubTableDetail}
              setGetSubSubTableDetail={setGetSubSubTableDetail}
              setGetSubSubTableAdd={setGetSubSubTableAdd}
              setGetSubSubTableEditDirect={setGetSubSubTableEditDirect}
              setTableOriginalFunc={setTableOriginalFunc}
              setGetSubTableAdd={setGetSubTableAdd}
              mainRow={row}
            />
          );
        }
      })}
    </Fragment>
  );
}

interface ConstTableProps {
  getTableData: (edit: boolean) => void;
  tableType: "main" | "sub";
  serchOpen: boolean;
  serch?: boolean;
  setSerchOpen?: (serchOpen: boolean) => void;
  aiChatOpen?: boolean;
  tableColumn: TableColumn;
  dataColumn: DataColumn[];
  rows: dataDifine[];
  displayNumber: number;
  setDisplayNumber: (displayNumber: number) => void;
  displayNumbers: number[];
  pageNumber: number;
  setPageNumber: (displayNumber: number) => void;
  maxPageNumber: number;
  totalCount?: number;
  getDataDetail: (key: { [key: string]: string }, edit: boolean) => void;
  getSubTableName: {
    name: string;
    key: { [key: string]: string };
    subData: subDataDifine[];
  };
  setGetSubTableName: (value: {
    name: string;
    key: { [key: string]: string };
    subData: subDataDifine[];
  }) => void;
  getSubTableDetail: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  };
  setGetSubTableDetail: (value: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  }) => void;
  setGetSubTableAdd: (value: { name: string; row: dataDifine | null }) => void;
  setGetSubTableEditDirect: (value: EditDataDifine | null) => void;
  getSubSubTableName: {
    name: string;
    key: { [key: string]: string };
    subData: subDataDifine[];
  };
  setGetSubSubTableName: (value: {
    name: string;
    key: { [key: string]: string };
    subData: subDataDifine[];
  }) => void;
  getSubSubTableDetail: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  };
  setGetSubSubTableDetail: (value: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  }) => void;
  setGetSubSubTableAdd: (value: {
    name: string;
    row: dataDifine | null;
  }) => void;
  setGetSubSubTableEditDirect: (value: EditDataDifine | null) => void;
  tableTitle: string;
  switchTableCheckBox: (dataKey: { [key: string]: string }) => void;
  allCheckFlg: boolean;
  switchAllTableCheckBox: () => void;
  editIDs: number[];
  setEditIDs: (value: number[]) => void;
  setEditDatasName: (name: string) => void;
  setTableOriginalFunc: (value: {
    name: string;
    key: { [key: string]: string };
    subTableColumnName?: string;
  }) => void;
  sortColumn: {
    key: string;
    sort: string;
  }[];
  setSortColumn: (
    value: {
      key: string;
      sort: string;
    }[],
  ) => void;
  emptyState?: React.ReactNode;
}

export function ConstTable(props: ConstTableProps) {
  const {
    getTableData,
    tableType,
    serchOpen,
    serch,
    setSerchOpen,
    aiChatOpen = false,
    tableColumn,
    dataColumn,
    rows,
    displayNumber,
    setDisplayNumber,
    displayNumbers,
    pageNumber,
    setPageNumber,
    maxPageNumber,
    totalCount,
    getDataDetail,
    getSubTableName,
    setGetSubTableName,
    getSubTableDetail,
    setGetSubTableDetail,
    setGetSubTableAdd,
    setGetSubTableEditDirect,
    getSubSubTableName,
    setGetSubSubTableName,
    getSubSubTableDetail,
    setGetSubSubTableDetail,
    setGetSubSubTableAdd,
    setGetSubSubTableEditDirect,
    tableTitle,
    switchTableCheckBox,
    allCheckFlg,
    switchAllTableCheckBox,
    editIDs,
    setEditIDs,
    setEditDatasName,
    setTableOriginalFunc,
    sortColumn,
    setSortColumn,
    emptyState,
  } = props;

  const loginUser = useAtomValue(AppAtoms.loginUser);
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const pathname = useAtomValue(AppAtoms.pathname);
  const listScroll = useAtomValue(AppAtoms.listScrollByRoute);
  const setListScroll = useSetAtom(AppAtoms.listScrollByRoute);
  const tableHeaderRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    const handleScroll = () => {
      if (tableHeaderRef.current && isSmartPhone) {
        tableHeaderRef.current.style.left = `-${window.scrollX}px`;
      }
    };
    window.addEventListener("scroll", handleScroll);

    // クリーンアップ
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const [pageList, setPageList] = useState<any>([]);
  useEffect(() => {
    const pageList = [];
    for (let i = 1; i <= maxPageNumber; i++) {
      pageList.push(
        <MenuItem key={"maxPageNumber" + i} value={i}>
          {i}
        </MenuItem>,
      );
    }
    setPageList(pageList);
  }, [maxPageNumber]);

  const [screenDataColumn, setScreenDataColumn] = useState<DataColumn[]>([]);
  useEffect(() => {
    setScreenDataColumn(
      dataColumn.length > 0 ? dataColumn : tableColumn.dataColumn,
    );
  }, [tableColumn, dataColumn]);

  // スクロール位置保存ヘルパー（詳細オープン直前に呼ぶ）
  const saveScrollPos = (rowId: string) => {
    const container = document.querySelector('[data-testid="table-scroll-container"]');
    if (!container) return;
    setListScroll((prev) => ({
      ...prev,
      [pathname]: { top: container.scrollTop, left: container.scrollLeft, rowId },
    }));
  };

  // 一覧再マウント後のスクロール位置・行ハイライト復元（consume-once・rw2）
  // deps: rows?.length + screenDataColumn.length（列が入る前は走らせない=clamp防止）
  useEffect(() => {
    if (!rows || rows.length === 0) return;
    if (screenDataColumn.length === 0) return; // 列未確定=scrollWidth未確定=clamp防止
    const saved = listScroll[pathname];
    if (!saved) return;
    const container = document.querySelector('[data-testid="table-scroll-container"]');
    if (!container) return;
    // rAF 内で layout 確定後に適用（列非同期到着によるscrollWidth未確定を回避）
    // consume-once は適用後（rAF 内）: 先に削除するとclampで0になった値が確定してしまう
    requestAnimationFrame(() => {
      container.scrollTop = saved.top;
      container.scrollLeft = saved.left;
      if (saved.rowId) {
        const rowEl = container.querySelector(`[data-row-id="${saved.rowId}"]`);
        if (rowEl) {
          rowEl.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          container.scrollLeft = saved.left; // scrollIntoView の scrollLeft 副作用を打消し
          (rowEl as HTMLElement).classList.add('row-highlight');
          setTimeout(() => {
            (rowEl as HTMLElement).classList.remove('row-highlight');
          }, 3000);
        }
      }
      // consume-once: 適用後に削除（同一一覧での rows 再取得で誤って再ハイライトしない）
      setListScroll((prev) => {
        const next = { ...prev };
        delete next[pathname];
        return next;
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows?.length, screenDataColumn.length]);

  const pages = generatePageRange(pageNumber, maxPageNumber);
  const start = (pageNumber - 1) * displayNumber + 1;
  const end = rows ? start + rows.length - 1 : start + displayNumber - 1;

  const visibleDataCols = screenDataColumn.filter(
    (col) =>
      col.table &&
      loginUser &&
      col.tableDetailAuth.includes(loginUser.authority),
  );
  const visibleSubTables = tableColumn.subTable.filter(
    (st) => loginUser && st.subTableAuth.includes(loginUser.authority),
  );

  const isRowSubTableOpen = (row: dataDifine, subTableColumnName: string) => {
    if (getSubTableName.name !== subTableColumnName) return false;
    return tableColumn.detailKey.every(
      (k) => getSubTableName.key[k] === String(row[k]),
    );
  };

  const toggleSubTable = (row: dataDifine, subTable: SubTableColumn) => {
    const isOpen = isRowSubTableOpen(row, subTable.subTableColumnName);
    if (isOpen) {
      setGetSubTableName({ name: "", key: {}, subData: [] });
    } else {
      const key: { [k: string]: string } = {};
      tableColumn.detailKey.forEach((k) => {
        key[k] = String(row[k]);
      });
      setGetSubTableName({
        name: subTable.subTableColumnName,
        key,
        subData: [],
      });
    }
  };

  // ─── SP cards ───────────────────────────────────────────
  if (isSmartPhone && tableType === "main") {
    const spTier = tableColumn.spTier ?? 'sp_first';

    // ─── SP b4-scroll（sp_readable: 横スクロール表形）───────
    if (spTier === 'sp_readable') {
      const visibleFuncs = tableColumn.tableFunction.filter(
        (tf) => loginUser && tf.auth.includes(loginUser.authority),
      );
      return (
        <Box
          data-testid="b4-scroll-table"
          sx={{
            overflowX: 'auto',
            width: '100%',
            '&::-webkit-scrollbar': { height: '8px' },
            '&::-webkit-scrollbar-thumb': {
              background: 'var(--color-border)',
              borderRadius: '4px',
            },
          }}
        >
          <Table
            size="small"
            sx={{
              minWidth: '760px',
              width: '100%',
              borderCollapse: 'collapse',
              backgroundColor: 'var(--color-white)',
              // cmd-po_1244 rework1(F2相当): SubTableが入るとヘッダーが濃紺on濃紺になるため文字系4規則を追記（カード枝:3252と同内容）。b4-scroll自身のthead/tbodyは値同一のため見た目不変
              "& thead .table-header": {
                fontSize: "11px",
                fontWeight: 600,
                color: "var(--color-white)",
              },
              "& thead .MuiTableCell-root": {
                color: "var(--color-white)",
              },
              "& tbody .table-body": {
                fontSize: "var(--fs-12)",
                color: "var(--color-sumi)",
              },
              "& tbody .MuiTableCell-root": {
                fontSize: "var(--fs-12)",
                color: "var(--color-sumi)",
              },
            }}
          >
            <TableHead>
              <TableRow>
                {visibleDataCols.map((col, idx) => (
                  <TableCell
                    key={col.key}
                    sx={{
                      fontSize: 'var(--fs-11)',
                      fontWeight: 'var(--fw-semibold)',
                      color: 'var(--color-white)',
                      backgroundColor: 'var(--color-ai)',
                      padding: 'var(--sp-2) 6px',
                      ...(idx === 0
                        ? {
                            position: 'sticky',
                            left: 0,
                            zIndex: 2,
                            boxShadow: '2px 0 4px rgba(30,58,95,0.06)',
                          }
                        : {}),
                    }}
                  >
                    {col.name}
                  </TableCell>
                ))}
                {visibleSubTables.map((st) => (
                  <TableCell
                    key={st.subTableColumnName}
                    sx={{
                      fontSize: 'var(--fs-11)',
                      fontWeight: 'var(--fw-semibold)',
                      color: 'var(--color-white)',
                      backgroundColor: 'var(--color-ai)',
                      padding: 'var(--sp-2) 6px',
                    }}
                  >
                    {st.subTableColumnName}
                  </TableCell>
                ))}
                {visibleFuncs.map((tf) => (
                  <TableCell
                    key={tf.name}
                    sx={{
                      fontSize: 'var(--fs-11)',
                      fontWeight: 'var(--fw-semibold)',
                      color: 'var(--color-white)',
                      backgroundColor: 'var(--color-ai)',
                      padding: 'var(--sp-2) 6px',
                    }}
                  >
                    {tf.name}
                  </TableCell>
                ))}
                {tableColumn.detail && (
                  <TableCell
                    sx={{
                      fontSize: 'var(--fs-11)',
                      fontWeight: 'var(--fw-semibold)',
                      color: 'var(--color-white)',
                      backgroundColor: 'var(--color-ai)',
                      padding: 'var(--sp-2) 6px',
                    }}
                  >
                    {tableColumn.detailColumn}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                const bg = getRowBg(pathname, row, index);
                const detailKey: { [k: string]: string } = {};
                tableColumn.detailKey.forEach((k) => {
                  detailKey[k] = String(row[k]);
                });
                return (
                  // cmd-po_1244(R41): 行の直後の兄弟として「開いているサブテーブル」を並べるためFragmentで内包（keyはFragmentへ移設）
                  <Fragment key={index}>
                  <TableRow data-row-id={row.id != null ? String(row.id) : undefined}>
                    {visibleDataCols.map((col, colIdx) => (
                      <TableCell
                        key={col.key}
                        sx={{
                          fontSize: 'var(--fs-12)',
                          color: 'var(--color-sumi)',
                          padding: 'var(--sp-2) 6px',
                          backgroundColor: bg ?? 'var(--color-white)',
                          ...(colIdx === 0
                            ? {
                                position: 'sticky',
                                left: 0,
                                zIndex: 2,
                                backgroundColor: bg ?? 'var(--color-white)',
                                boxShadow: '2px 0 4px rgba(30,58,95,0.06)',
                              }
                            : {}),
                        }}
                      >
                        {row[col.key] as string}
                      </TableCell>
                    ))}
                    {visibleSubTables.map((st) => {
                      const open = isRowSubTableOpen(row, st.subTableColumnName);
                      const isDisabled =
                        String(row[st.subTableColumnLength + '_disable']) === '0';
                      const count = row[st.subTableColumnLength] as number | undefined;
                      const isSingle = pathname === '/toss_up' && count === 1;
                      return (
                        <TableCell
                          key={st.subTableColumnName}
                          sx={{
                            fontSize: 'var(--fs-12)',
                            color: 'var(--color-sumi)',
                            padding: 'var(--sp-2) 6px',
                            backgroundColor: bg ?? 'var(--color-white)',
                          }}
                        >
                          {isDisabled ? (
                            <Typography
                              data-testid="subtable-unavailable"
                              sx={{
                                fontSize: 'var(--fs-11)',
                                fontWeight: 'var(--fw-semibold)',
                                color: 'var(--state-disabled-fg, #9e9e9e)',
                              }}
                            >
                              -
                            </Typography>
                          ) : isSingle ? (
                            <Typography sx={{ fontSize: 'var(--fs-12)' }}>
                              {row[st.subTableColumnLength + '_name'] as string}
                            </Typography>
                          ) : (
                            <IconButton
                              size="small"
                              sx={{
                                height: '22px',
                                width: '22px',
                                color: 'var(--color-ai)',
                              }}
                              onClick={() => toggleSubTable(row, st)}
                            >
                              {open ? (
                                <ExpandLessIcon sx={{ fontSize: '12px' }} />
                              ) : (
                                <ExpandMoreIcon sx={{ fontSize: '12px' }} />
                              )}
                            </IconButton>
                          )}
                        </TableCell>
                      );
                    })}
                    {visibleFuncs.map((tf) => (
                      <TableCell
                        key={tf.name}
                        sx={{
                          padding: 'var(--sp-2) 6px',
                          backgroundColor: bg ?? 'var(--color-white)',
                        }}
                      >
                        <IconButton
                          data-testid="b4-fn-btn"
                          size="small"
                          sx={{ height: '30px', width: '30px' }}
                          onClick={() =>
                            setTableOriginalFunc({ name: tf.name, key: detailKey })
                          }
                        >
                          <MoreHorizIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    ))}
                    {tableColumn.detail && (
                      <TableCell
                        sx={{
                          padding: 'var(--sp-2) 6px',
                          backgroundColor: bg ?? 'var(--color-white)',
                        }}
                      >
                        <IconButton
                          data-testid="b4-fn-btn"
                          disabled={row.id === '-'}
                          size="small"
                          sx={{ height: '30px', width: '30px' }}
                          onClick={() => {
                            saveScrollPos(row.id != null ? String(row.id) : '');
                            getDataDetail(detailKey, false);
                          }}
                        >
                          <MoreHorizIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                  {/* cmd-po_1244(R41): PC枝:2438-2475相当。既に表の中なので包まず兄弟行として置く。開はisRowSubTableOpenで判定・閉は<SubTable>内Collapse unmountOnExitでDOMごと消える */}
                  {visibleSubTables
                    .filter((st) => isRowSubTableOpen(row, st.subTableColumnName))
                    .map((st) => (
                      <SubTable
                        key={st.subTableColumnName}
                        open={isRowSubTableOpen(row, st.subTableColumnName)}
                        subTableColumn={st}
                        getSubTableName={getSubTableName}
                        getSubTableDetail={getSubTableDetail}
                        setGetSubTableDetail={setGetSubTableDetail}
                        setGetSubTableEditDirect={setGetSubTableEditDirect}
                        getSubSubTableName={getSubSubTableName}
                        setGetSubSubTableName={setGetSubSubTableName}
                        getSubSubTableDetail={getSubSubTableDetail}
                        setGetSubSubTableDetail={setGetSubSubTableDetail}
                        setGetSubSubTableAdd={setGetSubSubTableAdd}
                        setGetSubSubTableEditDirect={setGetSubSubTableEditDirect}
                        setTableOriginalFunc={setTableOriginalFunc}
                        setGetSubTableAdd={setGetSubTableAdd}
                        mainRow={row}
                      />
                    ))}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      );
    }

    // ─── SP card-list（sp_first: カード形）──────────────────
    return (
      <Box data-testid="sp-card-list" sx={{ p: 0, display: "flex", flexDirection: "column", gap: "var(--sp-3, 12px)" }}>
        {rows.map((row, index) => {
          const bg = getRowBg(pathname, row, index);
          const detailKey: { [k: string]: string } = {};
          tableColumn.detailKey.forEach((k) => {
            detailKey[k] = String(row[k]);
          });

          if (pathname === "/follow_call") {
            const fcStatus = row.follow_call_status as string;
            const fcSpColKeys = [
              "customer_phone_number",
              "product_name",
              "contract_number",
              "follow_call_date",
              "follow_call_hope_time",
              "follow_call_memo",
            ];
            const fcColDefs = fcSpColKeys
              .map((k) => tableColumn.dataColumn.find((c) => c.key === k))
              .filter((c): c is NonNullable<typeof c> => c != null);
            return (
              <Box
                key={index}
                data-testid="follow-call-card"
                data-row-id={row.id != null ? String(row.id) : undefined}
                sx={{
                  bgcolor: bg ?? "background.paper",
                  borderRadius: "var(--radius-md, 8px)",
                  border: "1px solid var(--color-border, #e5e5e0)",
                  p: "var(--sp-3, 12px) var(--sp-4, 16px)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--sp-1, 4px)",
                  boxShadow: "var(--shadow-card, 0 1px 3px rgba(0,0,0,.08))",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography
                    sx={{ fontSize: "13px", fontWeight: 600 }}
                    
                  >
                    {row.customer_name as string}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "11px", color: "text.secondary" }}
                  >
                    {fcStatus}
                  </Typography>
                </Box>
                {fcColDefs.map((col) => (
                  <Box
                    key={col.key}
                    sx={{ display: "flex", gap: 1, fontSize: "12px", mb: 0.5 }}
                  >
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "text.secondary",
                        minWidth: "80px",
                        flexShrink: 0,
                      }}
                    >
                      {col.name}
                    </Typography>
                    <Typography sx={{ fontSize: "12px" }}>
                      {row[col.key] as string}
                    </Typography>
                  </Box>
                ))}
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1, fontSize: "12px" }}
                  onClick={() =>
                    setTableOriginalFunc({
                      name: "フォローコール",
                      key: detailKey,
                    })
                  }
                >
                  コール記録
                </Button>
              </Box>
            );
          }

          return (
            <Box
              key={index}
              data-testid="table-card"
              data-row-id={row.id != null ? String(row.id) : undefined}
              sx={{
                bgcolor: bg ?? "background.paper",
                borderRadius: "var(--radius-md, 8px)",
                border: "1px solid var(--color-border, #e5e5e0)",
                p: "var(--sp-3, 12px) var(--sp-4, 16px)",
                display: "flex",
                flexDirection: "column",
                gap: "var(--sp-1, 4px)",
                boxShadow: "var(--shadow-card, 0 1px 3px rgba(0,0,0,.08))",
              }}
            >
              {/* cmd-po_1245: SPカード左上の内部DB行ID(見出しTypography row[detailKey[0]])を除去し詳細ボタンだけを右端(flex-end)に残す。詳細ボタンを持たない画面(detail=false)ではヘッダー行BoxごとDOM非描画=(1)で空になった行のgap隙間を出さない */}
              {tableColumn.detail && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                  }}
                >
                  {tableColumn.detail && (
                    <IconButton
                      disabled={row.id === "-"}
                      size="small"
                      sx={{ height: "28px", width: "28px" }}
                      onClick={() => {
                        saveScrollPos(row.id != null ? String(row.id) : '');
                        getDataDetail(detailKey, false);
                      }}
                    >
                      <MoreHorizIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              )}
              {visibleDataCols.map((col) => (
                <Box
                  key={col.key}
                  sx={{
                    display: "flex",
                    gap: 1,
                    fontSize: "12px",
                    mb: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "text.secondary",
                      minWidth: "80px",
                      flexShrink: 0,
                    }}
                  >
                    {col.name}
                  </Typography>
                  <Typography sx={{ fontSize: "12px" }}>
                    {row[col.key] as string}
                  </Typography>
                </Box>
              ))}
              {visibleSubTables.length > 0 && (
                <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
                  {visibleSubTables.map((st) => {
                    const isDisabled =
                      String(row[st.subTableColumnLength + "_disable"]) === "0";
                    const count = row[st.subTableColumnLength] as
                      | number
                      | undefined;
                    const isSingle = pathname === "/toss_up" && count === 1;
                    const open = isRowSubTableOpen(row, st.subTableColumnName);
                    return (
                      <Box
                        key={st.subTableColumnName}
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        {/* catalog #05 sub-chev .n: fs-11 / color-sumi-muted */}
                        <Typography
                          sx={{ fontSize: "var(--fs-11)", color: "var(--color-sumi-muted)" }}
                        >
                          {st.subTableColumnName}
                        </Typography>
                        {isDisabled ? (
                          // catalog #05 sub-chev.disabled: color-state-disabled-fg
                          <Typography
                            data-testid="subtable-unavailable"
                            sx={{
                              fontSize: "var(--fs-11)",
                              fontWeight: "var(--fw-semibold)",
                              color: "var(--state-disabled-fg, #9e9e9e)",
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            -
                          </Typography>
                        ) : isSingle ? (
                          <Typography
                            className="sub-single"
                            sx={{ fontSize: "var(--fs-12)" }}
                          >
                            {row[st.subTableColumnLength + "_name"] as string}
                          </Typography>
                        ) : (
                          // catalog #05 sub-chev: color-ai / icon 12px
                          <IconButton
                            size="small"
                            sx={{ height: "22px", width: "22px", color: "var(--color-ai)" }}
                            onClick={() => toggleSubTable(row, st)}
                          >
                            {open ? (
                              <ExpandLessIcon sx={{ fontSize: "12px" }} />
                            ) : (
                              <ExpandMoreIcon sx={{ fontSize: "12px" }} />
                            )}
                          </IconButton>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
              {/* cmd-po_1244(R41): chevron並びのすぐ下・カード内に「開いているサブテーブル」を描画。SubTableのrootはTableRow/TableCellなのでカード(Box)内では<Table size="small"><TableBody>で内包。閉はCollapse unmountOnExitでDOMごと消える。propは PC枝:2452-2472 をそのまま流用 */}
              {visibleSubTables
                .filter((st) => isRowSubTableOpen(row, st.subTableColumnName))
                .map((st) => (
                  <Box
                    key={st.subTableColumnName}
                    data-testid="sp-card-subtable"
                    // cmd-po_1244 rework1(F1): 幅の広いサブテーブルはこの器の中で横スクロール（カード外へはみ出させない・ページ横スクロールも出さない）
                    sx={{ mt: 1, overflowX: "auto" }}
                  >
                    {/* cmd-po_1244 rework1(F2): カード内では外側main Tableの入れ子にならないためthead白文字規則が届かない。main Table(:3348-)の文字系規則だけを複製（背景/sticky/paddingはSubTable自身のsx(:1196)が供給するため複製しない） */}
                    <Table
                      size="small"
                      sx={{
                        "& thead .table-header": {
                          fontSize: "11px",
                          fontWeight: 600,
                          color: "var(--color-white)",
                        },
                        "& thead .MuiTableCell-root": {
                          color: "var(--color-white)",
                        },
                        "& tbody .table-body": {
                          fontSize: "var(--fs-12)",
                          color: "var(--color-sumi)",
                        },
                        "& tbody .MuiTableCell-root": {
                          fontSize: "var(--fs-12)",
                          color: "var(--color-sumi)",
                        },
                      }}
                    >
                      <TableBody>
                        <SubTable
                          open={isRowSubTableOpen(row, st.subTableColumnName)}
                          subTableColumn={st}
                          getSubTableName={getSubTableName}
                          getSubTableDetail={getSubTableDetail}
                          setGetSubTableDetail={setGetSubTableDetail}
                          setGetSubTableEditDirect={setGetSubTableEditDirect}
                          getSubSubTableName={getSubSubTableName}
                          setGetSubSubTableName={setGetSubSubTableName}
                          getSubSubTableDetail={getSubSubTableDetail}
                          setGetSubSubTableDetail={setGetSubSubTableDetail}
                          setGetSubSubTableAdd={setGetSubSubTableAdd}
                          setGetSubSubTableEditDirect={setGetSubSubTableEditDirect}
                          setTableOriginalFunc={setTableOriginalFunc}
                          setGetSubTableAdd={setGetSubTableAdd}
                          mainRow={row}
                        />
                      </TableBody>
                    </Table>
                  </Box>
                ))}
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <Box
      data-testid={tableType === "main" ? "table-card" : undefined}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        boxShadow: tableType == "main" ? "var(--shadow-soft)" : undefined,
        border:
          tableType == "main"
            ? "1px solid var(--color-border)"
            : undefined,
        borderRadius: tableType == "main" ? "var(--radius-md)" : undefined,
        backgroundColor: tableType == "main" ? "var(--color-white)" : undefined,
      }}
    >
      <TableContainer
        data-testid="table-scroll-container"
        component={Paper}
        elevation={
          isSmartPhone ? undefined : tableType == "main" ? 0 : undefined
        }
        sx={{
          margin: isSmartPhone
            ? undefined
            : tableType == "main"
              ? undefined
              : "20px 4px 0px",
          width: isSmartPhone
            ? undefined
            : tableType == "main"
              ? undefined
              : "calc(100% - 8px)",
          height: isSmartPhone
            ? undefined
            : tableType == "main"
              ? undefined
              : "calc(100% - 12px)",
          flex: isSmartPhone
            ? undefined
            : tableType == "main"
              ? "1 1 auto"
              : undefined,
          minHeight: isSmartPhone
            ? undefined
            : tableType == "main"
              ? 0
              : undefined,
          border: isSmartPhone
            ? undefined
            : tableType == "main"
              ? undefined
              : "1px solid rgb(230, 230, 230)",
          marginTop: isSmartPhone
            ? tableType == "main"
              ? "165px"
              : undefined
            : tableType == "main"
              ? undefined
              : undefined,
          position: isSmartPhone && tableType == "sub" ? "fixed" : undefined,
          bottom: isSmartPhone && tableType == "sub" ? "0px" : undefined,
        }}
      >
        <Table
          stickyHeader
          size="small"
          aria-label="collapsible table"
          sx={{
            "& thead .table-header": {
              fontSize: "11px",
              fontWeight: 600,
              color: "var(--color-white)",
            },
            "& thead .MuiTableCell-root": {
              // catalog #05 po-table.compact th: padding var(--sp-2) 6px / fs-11 / fw-semibold / color-ai
              padding: "var(--sp-2) 6px",
              whiteSpace: "nowrap",
              fontSize: "var(--fs-11)",
              fontWeight: "var(--fw-semibold)",
              color: "var(--color-white)",
              background: "var(--color-ai)",
              borderBottom: "1px solid var(--color-border)",
            },
            "& thead .MuiTableCell-root:first-of-type": {
              paddingLeft: "var(--sp-3)",  // compact first-child: 12px
            },
            "& tbody .table-body": {
              // catalog #05 po-table td: fs-12 / color-sumi
              fontSize: "var(--fs-12)",
              color: "var(--color-sumi)",
            },
            "& tbody .MuiTableCell-root": {
              // catalog #05 po-table.compact td: padding var(--sp-2) 6px
              padding: "var(--sp-2) 6px",
              fontSize: "var(--fs-12)",
              color: "var(--color-sumi)",
              borderBottom: "1px solid var(--color-border-light)",
            },
            "& tbody .MuiTableCell-root:first-of-type": {
              paddingLeft: "var(--sp-3)",  // compact first-child: 12px
            },
          }}
        >
          <TableHead>
            <TableRow
              ref={tableHeaderRef}
              sx={{
                position:
                  isSmartPhone && tableType == "main" ? "fixed" : undefined,
                top: isSmartPhone && tableType == "main" ? "128px" : undefined,
                zIndex: isSmartPhone && tableType == "main" ? 1 : undefined,
              }}
            >
              {screenDataColumn.map((dataColumn, i) => {
                if (dataColumn.table) {
                  if (dataColumn.key === "contract_number_months") {
                    return getOneYearList().map((yearMonth, year_i) => {
                      return (
                        <TableCell
                          sx={{
                            backgroundColor: "var(--color-ai)",
                            minWidth: dataColumn.tableMinWidth,
                            maxWidth: isSmartPhone
                              ? dataColumn.tableMinWidth
                              : undefined,
                          }}
                          key={"tableYearMonthColumn" + year_i}
                          align="left"
                        >
                          <Box className="table-header">{yearMonth}</Box>
                        </TableCell>
                      );
                    });
                  } else {
                    if (
                      loginUser &&
                      dataColumn.tableDetailAuth.includes(loginUser.authority)
                    ) {
                      if (dataColumn.tableValue == "edit_check") {
                        return (
                          <TableCell
                            sx={{
                              backgroundColor: "var(--color-ai)",
                              minWidth: dataColumn.tableMinWidth,
                              maxWidth: isSmartPhone
                                ? dataColumn.tableMinWidth
                                : undefined,
                            }}
                            key={"tableColumn" + i}
                            align="center"
                          >
                            <IconButton
                              disabled={editIDs.length === 0}
                              sx={{
                                height: "30px",
                                width: dataColumn.tableMinWidth,
                                color: "var(--color-white, #fff)",
                                "&:hover": {
                                  backgroundColor: "rgba(255,255,255,0.12)",
                                },
                              }}
                              aria-label="detail row"
                              size="small"
                              onClick={() => setEditDatasName(dataColumn.name)}
                            >
                              <Box className="table-header">
                                {dataColumn.name}
                              </Box>
                              <EditNoteIcon
                                fontSize="medium"
                                sx={{
                                  "& .MuiSvgIcon-root": {
                                    fontSize: 18,
                                  },
                                  marginLeft: "5px",
                                }}
                              />
                            </IconButton>
                          </TableCell>
                        );
                      } else if (dataColumn.tableValue == "check") {
                        return (
                          <TableCell
                            sx={{
                              backgroundColor: "var(--color-ai)",
                              minWidth: dataColumn.tableMinWidth,
                              maxWidth: isSmartPhone
                                ? dataColumn.tableMinWidth
                                : undefined,
                            }}
                            key={"tableColumn" + i}
                            align="center"
                          >
                            <Box className="table-header">
                              {dataColumn.name}
                              <IconButton
                                disabled={rows.length === 0}
                                sx={{
                                  height: "30px",
                                  width: "30px",
                                  marginTop: "-5px",
                                }}
                                aria-label="detail row"
                                size="small"
                                onClick={switchAllTableCheckBox}
                              >
                                <Checkbox
                                  disabled={rows.length === 0}
                                  checked={
                                    rows.length === 0 ? false : allCheckFlg
                                  }
                                  sx={{
                                    "& .MuiSvgIcon-root": { fontSize: 18 },
                                    color: "var(--color-white, #fff)",
                                    "&.Mui-checked": {
                                      color: "var(--color-white, #fff)",
                                    },
                                  }}
                                />
                              </IconButton>
                            </Box>
                          </TableCell>
                        );
                      } else {
                        return (
                          <TableCell
                            sx={{
                              backgroundColor: "var(--color-ai)",
                              minWidth: dataColumn.tableMinWidth,
                              maxWidth: isSmartPhone
                                ? dataColumn.tableMinWidth
                                : undefined,
                            }}
                            key={"tableColumn" + i}
                            align={
                              dataColumn.tableValue === "downloadPaymentExcel"
                                ? "center"
                                : "left"
                            }
                          >
                            <Box className="table-header">
                              {dataColumn.name}
                              {dataColumn.sort ? (
                                <IconButton
                                  sx={{
                                    height: "30px",
                                    width: "30px",
                                    marginTop: "-5px",
                                    color: "var(--color-white, #fff)",
                                    "&:hover": {
                                      backgroundColor: "rgba(255,255,255,0.12)",
                                    },
                                  }}
                                  aria-label="detail row"
                                  size="small"
                                  onClick={() => {
                                    const isPrimary = sortColumn[0]?.key === dataColumn.key;
                                    const sort = isPrimary && sortColumn[0].sort === "DESC" ? "ASC" : "DESC";
                                    let _sortColumn = sortColumn.concat();
                                    _sortColumn = _sortColumn.filter(
                                      (column) => {
                                        return column.key !== dataColumn.key;
                                      },
                                    );
                                    _sortColumn.unshift({
                                      key: dataColumn.key,
                                      sort,
                                    });
                                    setSortColumn(_sortColumn);
                                  }}
                                >
                                  {sortColumn.some(
                                    (criterion) =>
                                      criterion.key === dataColumn.key &&
                                      criterion.sort === "DESC",
                                  ) ? (
                                    <ArrowDropDownIcon
                                      sx={{
                                        "& .MuiSvgIcon-root": { fontSize: 18 },
                                      }}
                                    />
                                  ) : (
                                    <ArrowDropUpIcon
                                      sx={{
                                        "& .MuiSvgIcon-root": { fontSize: 18 },
                                      }}
                                    />
                                  )}
                                </IconButton>
                              ) : null}
                            </Box>
                          </TableCell>
                        );
                      }
                    }
                  }
                }
              })}
              {tableColumn.subTable.map((subTable, subTable_i) => {
                if (
                  loginUser &&
                  subTable.subTableAuth.includes(loginUser.authority)
                ) {
                  return (
                    <>
                      <TableCell
                        sx={{
                          backgroundColor: "var(--color-ai)",
                          minWidth: subTable.subTableColumnNameWidth,
                          maxWidth: isSmartPhone
                            ? subTable.subTableColumnNameWidth
                            : undefined,
                        }}
                        key={"tableColumnSub" + subTable_i}
                        align="center"
                      >
                        <Box className="table-header">
                          {subTable.subTableColumnName}
                        </Box>
                      </TableCell>
                    </>
                  );
                }
              })}
              {tableColumn.detail ? (
                <TableCell
                  sx={{
                    backgroundColor: "var(--color-ai)",
                    minWidth: "60px",
                    maxWidth: isSmartPhone ? "60px" : undefined,
                  }}
                  key="tableColumnDetail"
                  align="center"
                >
                  <Box className="table-header">{tableColumn.detailColumn}</Box>
                </TableCell>
              ) : null}

              {tableColumn.tableFunction.map((tableFunction, i) => {
                if (
                  loginUser &&
                  tableFunction.auth.includes(loginUser.authority)
                ) {
                  return (
                    <TableCell
                      sx={{
                        backgroundColor: "var(--color-ai)",
                        minWidth: tableFunction.width,
                        maxWidth: isSmartPhone
                          ? tableFunction.width
                          : undefined,
                      }}
                      key={"tableColumnFunc" + i}
                      align="center"
                    >
                      <Box className="table-header">{tableFunction.name}</Box>
                    </TableCell>
                  );
                }
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && emptyState && (
              <TableRow>
                <TableCell colSpan={99} sx={{ border: "none", py: 4 }}>
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    {emptyState}
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row, index) => (
              <Row
                key={"row" + index}
                row={row}
                index={index}
                tableColumn={tableColumn}
                screenDataColumn={screenDataColumn}
                getDataDetail={getDataDetail}
                getSubTableName={getSubTableName}
                setGetSubTableName={setGetSubTableName}
                getSubTableDetail={getSubTableDetail}
                setGetSubTableDetail={setGetSubTableDetail}
                setGetSubTableAdd={setGetSubTableAdd}
                setGetSubTableEditDirect={setGetSubTableEditDirect}
                getSubSubTableName={getSubSubTableName}
                setGetSubSubTableName={setGetSubSubTableName}
                getSubSubTableDetail={getSubSubTableDetail}
                setGetSubSubTableDetail={setGetSubSubTableDetail}
                setGetSubSubTableAdd={setGetSubSubTableAdd}
                setGetSubSubTableEditDirect={setGetSubSubTableEditDirect}
                switchTableCheckBox={switchTableCheckBox}
                editIDs={editIDs}
                setEditIDs={setEditIDs}
                setTableOriginalFunc={setTableOriginalFunc}
                onDetailOpen={saveScrollPos}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {tableType == "main" ? (
        <Box
          data-testid="data-table-footer"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            px: 1,
            py: 0.75,
            borderTop: "1px solid var(--color-border, #e5e5e0)",
            bgcolor: "background.paper",
            flexShrink: 0,
            fontSize: "11px",
            color: "var(--color-sumi-muted)",
          }}
        >
          <Box component="span">
            {totalCount !== undefined ? `${totalCount}件中` : ""}
            {rows && rows.length > 0 ? `${start}〜${end}件を表示` : ""}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {pages.map((p, i) =>
              p === "..." ? (
                <Box
                  component="span"
                  key={`ellipsis-${i}`}
                  sx={{ fontSize: "12px", px: 0.5 }}
                >
                  ...
                </Box>
              ) : (
                <Box
                  key={p}
                  onClick={() => setPageNumber(p as number)}
                  sx={{
                    cursor: "pointer",
                    minWidth: "26px",
                    height: "26px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "4px",
                    fontSize: "12px",
                    fontWeight: p === pageNumber ? 600 : 400,
                    bgcolor:
                      p === pageNumber ? "var(--color-ai)" : "transparent",
                    color:
                      p === pageNumber ? "#fff" : "var(--color-sumi-light)",
                    "&:hover": {
                      bgcolor:
                        p === pageNumber ? "var(--color-ai)" : "action.hover",
                    },
                  }}
                >
                  {p}
                </Box>
              ),
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box component="span">表示数:</Box>
            {displayNumbers.map((n) => (
              <Box
                key={n}
                onClick={() => setDisplayNumber(n)}
                sx={{
                  cursor: "pointer",
                  px: 0.75,
                  py: 0.25,
                  borderRadius: "4px",
                  fontSize: "12px",
                  fontWeight: n === displayNumber ? 600 : 400,
                  color: "var(--color-sumi-light)",
                  bgcolor:
                    n === displayNumber ? "action.selected" : "transparent",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                {n}
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
